import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
// import ModifySelectionZF from "@/modules/selection/ModifySelectionZF";
import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SelectionResults } from "./SelectionResults";
import { ResultsBilanDisplayV2 } from "./ResultsBilanDisplayV2";
import ResultsZfDisplay from "./ResultsZfDisplay";

export interface Selection {
  id: string;
  timeline: string;
  patientId: string;
  selection: string;
  isDefault: boolean;
}

export interface SessionResults {
  id: number;
  sessionDate: string;
  resultsBilan: string;
  resultsZF: string;
  sujet: string;
  fiable: boolean;
  visualFieldInput: string;
  screenUsed: string;
  removedRows: number[];
  removedColumns: number[];
  order: number;
  sessionType: "BILAN" | "REEDUCATION";
  accountUsed: "PATIENT" | "CLINIQUE";
}

export default function ZFResultsTabs() {
  const [sessionsResultsZF, setSessionsResultsZF] = useState<SessionResults[]>(
    []
  );
  const [sessionsResultsBilan, setSessionsResultsBilan] = useState<
    SessionResults[]
  >([]);
  const [sessionResultsA, setSessionResultsA] = useState<SessionResults>();
  const [sessionResultsB, setSessionResultsB] = useState<SessionResults>();
  const [sessionResultsC, setSessionResultsC] = useState<SessionResults>();

  const { patientId } = useParams<{ patientId: string }>();

  // Navigation functions for ZF results
  const navigateZFA = (direction: 'prev' | 'next') => {
    if (!sessionResultsA || sessionsResultsZF.length === 0) return;
    
    const currentIndex = sessionsResultsZF.findIndex(session => session.id === sessionResultsA.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sessionsResultsZF.length - 1;
    } else {
      newIndex = currentIndex < sessionsResultsZF.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSessionResultsA(sessionsResultsZF[newIndex]);
  };

  const navigateZFB = (direction: 'prev' | 'next') => {
    if (!sessionResultsB || sessionsResultsZF.length === 0) return;
    
    const currentIndex = sessionsResultsZF.findIndex(session => session.id === sessionResultsB.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sessionsResultsZF.length - 1;
    } else {
      newIndex = currentIndex < sessionsResultsZF.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSessionResultsB(sessionsResultsZF[newIndex]);
  };

  const navigateBilan = (direction: 'prev' | 'next') => {
    if (!sessionResultsC || sessionsResultsBilan.length === 0) return;
    
    const currentIndex = sessionsResultsBilan.findIndex(session => session.id === sessionResultsC.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sessionsResultsBilan.length - 1;
    } else {
      newIndex = currentIndex < sessionsResultsBilan.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSessionResultsC(sessionsResultsBilan[newIndex]);
  };

  useEffect(() => {
    axios
      .get(`/practitioner/sessionsresults/bilan/${patientId}`)
      .then((response) => {
        const data: SessionResults[] = response.data;

        // On trie du plus récent au plus ancien :
        const sortedDataBilan = data.sort(
          (a, b) =>
            new Date(b.sessionDate).getTime() -
            new Date(a.sessionDate).getTime()
        );

        setSessionsResultsBilan(sortedDataBilan);
        // Sélection automatique des deux derniers bilans
        if (sortedDataBilan.length > 0) {
          setSessionResultsC(sortedDataBilan[0]); // le plus récent
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des données:", error);
      });

    axios
      .get(`/practitioner/sessionsresults/zf/${patientId}`)
      .then((response) => {
        const data: SessionResults[] = response.data;

        // On trie du plus récent au plus ancien :
        const sortedDataZF = data.sort(
          (a, b) =>
            new Date(b.sessionDate).getTime() -
            new Date(a.sessionDate).getTime()
        );

        setSessionsResultsZF(sortedDataZF);

        // Sélection automatique des deux derniers bilans
        if (sortedDataZF.length > 0) {
          setSessionResultsA(sortedDataZF[0]); // le plus récent
        }
        if (sortedDataZF.length > 1) {
          setSessionResultsB(sortedDataZF[1]); // le second plus récent
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des données:", error);
      });
  }, [patientId]);

  return (
    <Card className="w-5/6 h-5/6 pt-6" id="zf-results-card">
      {/* <CardHeader className="flex flex-col">
        <div className="w-full flex flex-col items-center space-y-6">
          <div>
            <CardTitle>Résultats Zone Frontière</CardTitle>
          </div>
        </div>
      </CardHeader> */}
      <CardContent className="flex flex-row">
        {/* Colonne de gauche : on affiche la vue ModifySelectionBilan ou ZF */}
        <div className="w-1/2 flex flex-col">
          <div className="flex flex-row">
            <div className="w-[90%]">
              <div className="mb-2 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateZFA('next')}
                  disabled={sessionsResultsZF.length <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1">
                  <SelectionResults
                    setSessionResults={setSessionResultsA}
                    sessionsResults={sessionsResultsZF}
                    currentSession={sessionResultsA}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateZFA('prev')}
                  disabled={sessionsResultsZF.length <= 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="mb-4 overflow-hidden">
                {sessionResultsA &&
                sessionResultsA.resultsZF !== null &&
                sessionResultsA.resultsZF !== undefined &&
                sessionResultsA.resultsZF !== "" &&
                sessionResultsA.resultsZF !== "null" ? (
                  <div 
                    id="zf-a-screen" 
                    data-caption={`ZF A — ${sessionResultsA?.sessionDate ? new Date(sessionResultsA.sessionDate).toLocaleDateString('fr-FR') : ''}`}
                  >
                    <ResultsZfDisplay resultData={sessionResultsA} />
                  </div>
                ) : (
                  "Veuillez sélectionner un résultat"
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-row mt-2">
            <div className="w-[90%]">
              <div className="overflow-hidden">
                <div className="mb-2 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateZFB('next')}
                    disabled={sessionsResultsZF.length <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex-1">
                    <SelectionResults
                      setSessionResults={setSessionResultsB}
                      sessionsResults={sessionsResultsZF}
                      currentSession={sessionResultsB}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateZFB('prev')}
                    disabled={sessionsResultsZF.length <= 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {sessionResultsB &&
                sessionResultsB.resultsZF !== null &&
                sessionResultsB.resultsZF !== undefined &&
                sessionResultsB.resultsZF !== "" &&
                sessionResultsB.resultsZF !== "null" ? (
                  <div 
                    id="zf-b-screen" 
                    data-caption={`ZF B — ${sessionResultsB?.sessionDate ? new Date(sessionResultsB.sessionDate).toLocaleDateString('fr-FR') : ''}`}
                  >
                    <ResultsZfDisplay resultData={sessionResultsB} />
                  </div>
                ) : (
                  "Veuillez sélectionner un résultat"
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="w-1/2 flex flex-col justify-around">
          <div>
              <strong>Bilan</strong>
            <div className="mb-2 flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateBilan('next')}
                disabled={sessionsResultsBilan.length <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <SelectionResults
                    setSessionResults={setSessionResultsC}
                    sessionsResults={sessionsResultsBilan}
                    currentSession={sessionResultsC}
                  />
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateBilan('prev')}
                disabled={sessionsResultsBilan.length <= 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {sessionResultsC &&
            sessionResultsC.resultsBilan !== null &&
            sessionResultsC.resultsBilan !== undefined &&
            sessionResultsC.resultsBilan !== "" &&
            sessionResultsC.resultsBilan !== "null" ? (
              <div 
                id="bilan-screen" 
                data-caption={`Bilan du ${sessionResultsC?.sessionDate ? new Date(sessionResultsC.sessionDate).toLocaleDateString('fr-FR') : ''}`}
              >
                <ResultsBilanDisplayV2 resultData={sessionResultsC} />
              </div>
            ) : (
              <div 
                id="bilan-screen" 
                data-caption="Bilan - Aucun résultat"
              >
                <ResultsBilanDisplayV2
                  resultData={{
                    resultsBilan: "",
                    sessionDate: "",
                    sujet: "",
                    fiable: false,
                    visualFieldInput: "",
                    screenUsed: "",
                    removedRows: [],
                    removedColumns: [],
                    id: 0,
                    resultsZF: "",
                    sessionType: "BILAN",
                    accountUsed: "PATIENT",
                    order: 0,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
